import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Text, Button, VStack } from "@chakra-ui/react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * T026: Error Boundary for Rainbow AI Radar
 *
 * Catches React errors in the radar component tree and displays
 * a user-friendly fallback UI instead of crashing the entire app.
 *
 * Features:
 * - Catches rendering errors, lifecycle errors, and constructor errors
 * - Shows fallback UI with error details (in development) or generic message (production)
 * - Allows user to retry by reloading the radar
 * - Logs errors for debugging
 */
class RadarErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error("RadarErrorBoundary caught an error:", error, errorInfo);

    // TODO: Send error to monitoring service (Sentry, etc.)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="rgba(255, 255, 255, 0.95)"
          p={6}
          borderRadius="md"
          boxShadow="lg"
          zIndex={9999}
          maxW="400px"
        >
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="bold" color="red.600">
              ⚠️ Radar Error
            </Text>

            <Text fontSize="sm" color="gray.700">
              The radar layer encountered an error and cannot be displayed.
            </Text>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                bg="gray.100"
                p={3}
                borderRadius="md"
                fontSize="xs"
                fontFamily="mono"
                overflow="auto"
                maxH="200px"
              >
                <Text color="red.700" fontWeight="bold" mb={2}>
                  Error Details (development only):
                </Text>
                <Text color="gray.800">{this.state.error.message}</Text>
                {this.state.error.stack && (
                  <Text color="gray.600" mt={2} fontSize="10px">
                    {this.state.error.stack}
                  </Text>
                )}
              </Box>
            )}

            <Button
              colorScheme="blue"
              size="sm"
              onClick={this.handleReset}
            >
              Retry
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default RadarErrorBoundary;
